import React from 'react';
import { Home, Trees, MapPin, TrendingUp, HeartPulse, Users } from 'lucide-react';
import { benefitsData, businessBenefits } from '../mock';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const iconMap = {
  home: Home,
  trees: Trees,
  'map-pin': MapPin,
  'trending-up': TrendingUp,
  'heart-pulse': HeartPulse,
  users: Users
};

export const Benefits = () => {
  return (
    <section id="benefits" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">
            Why Choose Cornwall?
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Discover the compelling reasons why individuals and businesses are making Cornwall their home
          </p>
        </div>

        {/* Tabs for Living vs Business */}
        <Tabs defaultValue="living" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
            <TabsTrigger value="living" className="text-lg">For Living</TabsTrigger>
            <TabsTrigger value="business" className="text-lg">For Business</TabsTrigger>
          </TabsList>

          {/* Living Benefits */}
          <TabsContent value="living" className="space-y-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefitsData.map((benefit) => {
                const IconComponent = iconMap[benefit.icon];
                return (
                  <Card
                    key={benefit.id}
                    className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-emerald-200 transform hover:-translate-y-1"
                  >
                    <CardHeader className="space-y-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center group-hover:from-emerald-500 group-hover:to-teal-600 transition-all duration-300">
                        <IconComponent className="w-7 h-7 text-emerald-600 group-hover:text-white transition-colors" />
                      </div>
                      <CardTitle className="text-xl font-bold text-slate-900">
                        {benefit.title}
                      </CardTitle>
                      <CardDescription className="text-slate-600 leading-relaxed">
                        {benefit.description}
                      </CardDescription>
                      <div className="pt-2 border-t border-slate-100">
                        <p className="text-xs text-emerald-600 font-medium">
                          Source: {benefit.data}
                        </p>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Business Benefits */}
          <TabsContent value="business" className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              {businessBenefits.map((benefit) => (
                <Card
                  key={benefit.id}
                  className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200 transform hover:-translate-y-1"
                >
                  <CardHeader className="space-y-4">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-2xl font-bold text-slate-900">
                        {benefit.title}
                      </CardTitle>
                      <div className="bg-gradient-to-br from-blue-100 to-cyan-100 px-3 py-1 rounded-full">
                        <span className="text-sm font-bold text-blue-700">
                          {benefit.savings}
                        </span>
                      </div>
                    </div>
                    <CardDescription className="text-slate-600 text-base leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-200">
              <div className="max-w-3xl mx-auto text-center space-y-4">
                <h3 className="text-2xl font-bold text-slate-900">
                  Ready to Grow Your Business in Cornwall?
                </h3>
                <p className="text-slate-600">
                  Join the thriving business community and take advantage of our competitive edge. 
                  Contact us at <a href="mailto:info@movetocornwall.ca" className="font-semibold text-blue-600 hover:text-blue-700">info@movetocornwall.ca</a>
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};